import { useCallback, useEffect, useRef, useState } from 'react'
import { Maximize2, Minimize2, ZoomIn, ZoomOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { attachVideo, type TileParticipant } from '@/lib/meetings/rtk'
import {
  FIT,
  isFit,
  panBy,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  zoomAt,
  type ZoomState,
} from '@/lib/meetings/share-zoom'
import { cn } from '@/lib/utils'

/**
 * A remote screen share, maximised (board m5gc #22/#23): the whole shared
 * screen fitted into the viewport (object-fit: contain, letterboxed, never
 * cropped), with zoom — buttons, ctrl/⌘-wheel, pinch — and drag-to-pan when
 * zoomed. The transform lives on the video element and is clamped so the
 * frame never leaves the box. Sits *under* the participants dock and the
 * control bar (z-30), so faces and controls stay reachable.
 */
export function ShareViewer({
  sharer,
  onMinimize,
}: {
  sharer: TileParticipant
  onMinimize: () => void
}) {
  const { t } = useTranslation('meet')
  const boxRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [zoom, setZoom] = useState<ZoomState>(FIT)
  /** Active pointers, for drag (one) and pinch (two). */
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const pinch = useRef<{ distance: number; cx: number; cy: number } | null>(null)
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null)

  useEffect(() => {
    if (videoRef.current) attachVideo(videoRef.current, sharer.screenShareTrack)
  }, [sharer.screenShareTrack])

  // A new share (new track) starts fitted.
  useEffect(() => setZoom(FIT), [sharer.screenShareTrack])

  const boxSize = useCallback(() => {
    const el = boxRef.current
    return { width: el?.clientWidth ?? 1, height: el?.clientHeight ?? 1 }
  }, [])
  const pointOf = (clientX: number, clientY: number) => {
    const rect = boxRef.current?.getBoundingClientRect()
    return { cx: clientX - (rect?.left ?? 0), cy: clientY - (rect?.top ?? 0) }
  }

  const zoomBy = (factor: number) =>
    setZoom((z) => {
      const box = boxSize()
      return zoomAt(z, factor, { cx: box.width / 2, cy: box.height / 2 }, box)
    })

  // ctrl/⌘ + wheel zooms around the cursor; a plain wheel pans when zoomed.
  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
        const factor = Math.exp(-event.deltaY * 0.0025)
        const point = pointOf(event.clientX, event.clientY)
        setZoom((z) => zoomAt(z, factor, point, boxSize()))
      } else {
        setZoom((z) => {
          if (isFit(z)) return z
          event.preventDefault()
          return panBy(z, { dx: -event.deltaX, dy: -event.deltaY }, boxSize())
        })
      }
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [boxSize])

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    event.currentTarget.setPointerCapture(event.pointerId)
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      pinch.current = {
        distance: Math.hypot(a!.x - b!.x, a!.y - b!.y),
        cx: (a!.x + b!.x) / 2,
        cy: (a!.y + b!.y) / 2,
      }
      drag.current = null
    } else if (pointers.current.size === 1) {
      drag.current = { x: event.clientX, y: event.clientY, moved: false }
    }
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (pinch.current && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()]
      const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y)
      const factor = distance / Math.max(1, pinch.current.distance)
      const point = pointOf((a!.x + b!.x) / 2, (a!.y + b!.y) / 2)
      pinch.current = { distance, cx: point.cx, cy: point.cy }
      setZoom((z) => zoomAt(z, factor, point, boxSize()))
      return
    }
    const d = drag.current
    if (!d) return
    const dx = event.clientX - d.x
    const dy = event.clientY - d.y
    if (Math.abs(dx) + Math.abs(dy) > 2) d.moved = true
    drag.current = { x: event.clientX, y: event.clientY, moved: d.moved }
    setZoom((z) => (isFit(z) ? z : panBy(z, { dx, dy }, boxSize())))
  }

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId)
    if (pointers.current.size < 2) pinch.current = null
    if (pointers.current.size === 0) drag.current = null
  }

  return (
    <div
      className="fixed inset-0 z-30 flex flex-col bg-black/90"
      data-testid="share-viewer"
      data-scale={zoom.scale.toFixed(2)}
    >
      <div
        ref={boxRef}
        data-testid="share-box"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={cn(
          'relative min-h-0 flex-1 touch-none overflow-hidden select-none',
          isFit(zoom) ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
        )}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          data-testid="share-video"
          className="h-full w-full object-contain"
          style={{
            transform: `translate(${zoom.tx}px, ${zoom.ty}px) scale(${zoom.scale})`,
            transformOrigin: 'center',
          }}
        />
        <div className="pointer-events-none absolute bottom-3 left-3 rounded-md bg-black/60 px-2 py-1 text-xs text-white">
          {t('share.viewing', { name: sharer.name })}
        </div>
      </div>

      <div className="absolute top-3 right-3 flex max-w-[calc(100%-1.5rem)] items-center gap-0.5 rounded-full border border-zinc-700 bg-zinc-900/95 p-1 text-white shadow-xl sm:gap-1">
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 rounded-full hover:bg-zinc-700 hover:text-white"
          aria-label={t('share.zoomOut')}
          data-testid="share-zoom-out"
          disabled={zoom.scale <= ZOOM_MIN}
          onClick={() => zoomBy(1 / ZOOM_STEP)}
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
        <span className="w-12 text-center text-xs tabular-nums" data-testid="share-zoom-level">
          {Math.round(zoom.scale * 100)}%
        </span>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 rounded-full hover:bg-zinc-700 hover:text-white"
          aria-label={t('share.zoomIn')}
          data-testid="share-zoom-in"
          disabled={zoom.scale >= ZOOM_MAX}
          onClick={() => zoomBy(ZOOM_STEP)}
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 rounded-full px-2 text-xs hover:bg-zinc-700 hover:text-white"
          aria-label={t('share.fit')}
          data-testid="share-fit"
          disabled={isFit(zoom)}
          onClick={() => setZoom(FIT)}
        >
          <Maximize2 className="h-3.5 w-3.5 sm:mr-1" />
          <span className="hidden sm:inline">{t('share.fit')}</span>
        </Button>
        <span className="mx-0.5 h-5 w-px bg-zinc-700" />
        <Button
          size="sm"
          variant="secondary"
          className="h-8 rounded-full px-2.5 text-xs"
          data-testid="share-minimize"
          onClick={onMinimize}
        >
          <Minimize2 className="h-3.5 w-3.5 sm:mr-1" />
          <span className="hidden sm:inline">{t('share.minimize')}</span>
        </Button>
      </div>
    </div>
  )
}
